import { formatDate } from '@angular/common';
import { Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { REPORT_COLUMNS, ReportColumn, ReportRow } from '@contracts';
import { DEFAULT_REPORT_COLUMNS } from '@feature/report';
import { ReportFacade } from '../facade/report.facade';
import { MenuHeaderTopic, TimeValue } from '@ui';
import * as ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { SelectionModel } from '@angular/cdk/collections';
import { DialogService, ToastService } from '@core';

@Component({
  selector: 'feature-report-page',
  templateUrl: './report.component.html',
  styleUrl: './report.component.scss',
  providers: [ReportFacade],
})
export class ReportComponent implements OnInit {
  @ViewChild(MatPaginator) paginator?: MatPaginator;
  @ViewChild(MatSort) sort?: MatSort;

  menuTopic: MenuHeaderTopic = {
    title: 'view_value.report',
    subtitle: 'view_value.report_list',
    iconUrl: 'assets/shared/icons/report.svg',
  };
  hours: string[] = [];
  minutes: string[] = [];
  displayedColumns: string[] = [];
  exportColumns: string[] = [];
  columns: ReportColumn[] = [];

  dataSource = new MatTableDataSource<any>([]);
  mocup: any[] = [];

  length = 0;
  pageSize = 10;
  pageSizeOptions = [10, 30, 50, 100];
  pageIndex = 0;
  pageEvent!: PageEvent;
  onDownload = false;
  selection = new SelectionModel<any>(true, []);
  myControl = new FormControl('');
  options: string[] = [];
  filteredOptions: string[] | any;

  filterForm = new FormGroup({
    building: new FormControl('all'),
    floor: new FormControl('all'),
    loop: new FormControl('all'),
    panel: new FormControl('all'),
    group_select: new FormControl('all'),
  });

  campaignOne = new FormGroup({
    select_start_time: new FormControl<any>(null),
    start_time: new FormControl<TimeValue | null>({
      value: { hour: 0, minute: 0 },
      disabled: true,
    }),
    select_end_time: new FormControl<any>({ value: null, disabled: true }),
    end_time: new FormControl<TimeValue | null>({
      value: { hour: 23, minute: 59 },
      disabled: true,
    }),
  });

  formMeter = new FormGroup({
    // module: new FormControl('RMS'),
    sensor_type: new FormControl('energy'),
  });

  sensor_type_list = ['energy', 'water'];

  private statusMap: Record<string, string> = {
    ใช้ไฟเพิ่มขึ้น: 'status--warning',
    ใช้ไฟปกติ: 'status--normal',
    ใช้ไฟสูงกว่าปกติ: 'status--over',
  };

  constructor(
    private translate: TranslateService,
    private dialogService: DialogService,
    private toastService: ToastService,
    private readonly facade: ReportFacade,
    @Optional() @Inject(REPORT_COLUMNS) reportColumns: ReportColumn[] | null,
  ) {
    this.columns = (
      reportColumns?.length ? reportColumns : DEFAULT_REPORT_COLUMNS
    ).filter((column) => column.visible !== false);

    this.displayedColumns = [
      'select',
      ...this.columns.map((column) => column.key),
    ];
    this.exportColumns = this.columns
      .filter((column) => column.exportable !== false)
      .map((column) => column.key);

    this.filterForm.valueChanges.subscribe(() => {
      this.applyLocalFilter();
    });
  }

  ngOnInit(): void {
    this.fetchData();

    this.hours = Array.from({ length: 24 }, (_, i) =>
      i.toString().padStart(2, '0'),
    );

    this.minutes = Array.from({ length: 12 }, (_, i) =>
      (i * 5).toString().padStart(2, '0'),
    );

    this.campaignOne.get('select_start_time')?.valueChanges.subscribe((val) => {
      if (val) {
        this.campaignOne.get('start_time')?.enable();
        this.campaignOne.get('select_end_time')?.enable();
      } else {
        this.campaignOne.get('start_time')?.disable();
      }
    });
    this.campaignOne.get('select_end_time')?.valueChanges.subscribe((val) => {
      if (val) {
        this.campaignOne.get('end_time')?.enable();
      } else {
        this.campaignOne.get('end_time')?.disable();
      }
    });
  }

  trackColumn(_: number, column: ReportColumn): string {
    return column.key;
  }

  getBgColor(value: string): string {
    return this.statusMap[value] ?? '';
  }

  getCellValue(row: any, column: ReportColumn, index: number): any {
    if (column.valueGetter) {
      return column.valueGetter(row, index, this.pageIndex, this.pageSize);
    }

    const field = column.field ?? column.key;
    return row[field];
  }

  getDecimalInfo(column: ReportColumn): string {
    const decimal = column.decimal;
    if (!decimal) return '1.0-0';

    const minIntegerDigits = decimal.minIntegerDigits ?? 1;
    const minFractionDigits = decimal.minFractionDigits ?? 0;
    const maxFractionDigits = decimal.maxFractionDigits ?? 0;

    return `${minIntegerDigits}.${minFractionDigits}-${maxFractionDigits}`;
  }

  onPageChange(event: PageEvent): void {
    this.pageEvent = event;
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
  }
  checkData(): void {
    const query: any = {
      sensor_type: this.formMeter.value.sensor_type,
    };

    if (this.campaignOne.value.select_start_time) {
      query.start_date = formatDate(
        this.campaignOne.value.select_start_time,
        'yyyy-MM-dd',
        'en-US',
      );
    }

    if (this.campaignOne.value.start_time) {
      const t = this.campaignOne.value.start_time as TimeValue;
      query.start_hours = `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}:00`;
    }

    if (this.campaignOne.value.select_end_time) {
      query.end_date = formatDate(
        this.campaignOne.value.select_end_time,
        'yyyy-MM-dd',
        'en-US',
      );
    }

    if (this.campaignOne.value.end_time) {
      const t = this.campaignOne.value.end_time as TimeValue;
      query.end_hours = `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}:00`;
    }

    if (
      this.campaignOne.value.select_start_time &&
      this.campaignOne.value.start_time &&
      this.campaignOne.value.select_end_time &&
      this.campaignOne.value.end_time
    )
      this.fetchData(query);
    else this.applyLocalFilter();
  }

  fetchData(query?: any): void {
    this.dialogService.withLoading(this.facade.load(query)).subscribe({
      next: (rows) => {
        this.mocup = rows;
        this.dataSource = new MatTableDataSource(rows);
        this.dataSource.paginator = this.paginator ?? null!;
        this.dataSource.sort = this.sort ?? null!;
        this.length = rows.length;
        this.onDownload = rows.length > 0;
        this.options = Array.from(
          new Set(
            rows
              .flatMap((row) => [row.room, row.shop_name])
              .filter((value): value is string => !!value),
          ),
        );
        this.toastService.success('view_value.load_data_success');

        this.selection.clear();
      },
      error: () => {
        this.mocup = [];
        this.dataSource = new MatTableDataSource<ReportRow>([]);
        this.length = 0;
        this.onDownload = false;
        this.toastService.error('view_value.load_data_error');
      },
    });
  }

  meterNameSearch(): void {
    if (!this.dataSource) return;
    this.dataSource.filter = (this.myControl.value ?? '')
      .toString()
      .trim()
      .toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  applyLocalFilter(): void {
    let data = [...this.mocup];
    const value = this.filterForm.getRawValue();

    if (value.group_select && value.group_select !== 'all') {
      data = data.filter((v) => v.group === value.group_select);
    }
    if (value.building && value.building !== 'all') {
      data = data.filter((v) => v.building === value.building);
    }
    if (value.floor && value.floor !== 'all') {
      data = data.filter((v) => v.floor === value.floor);
    }
    if (value.loop && value.loop !== 'all') {
      data = data.filter((v) => v.loop === value.loop);
    }
    if (value.panel && value.panel !== 'all') {
      data = data.filter((v) => v.panel === value.panel);
    }

    this.dataSource = new MatTableDataSource(data);
    this.dataSource.paginator = this.paginator ?? null!;
    this.dataSource.sort = this.sort ?? null!;
    this.length = data.length;
  }

  async onExportExcel(): Promise<void> {
    const rows =
      this.selectedCount > 0
        ? this.selection.selected
        : this.dataSource.filteredData?.length
          ? this.dataSource.filteredData
          : this.dataSource.data;

    const workbook = new ExcelJS.Workbook();

    const worksheet = workbook.addWorksheet('Meter Data');

    const headers = this.exportColumns.map((key) => {
      const col = this.columns.find((c) => c.key === key);
      return this.translate.instant(col?.label ?? key);
    });

    worksheet.addRow(headers);

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' },
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    worksheet.views = [{ state: 'frozen', ySplit: 1 }];

    rows.forEach((row, rowIndex) => {
      const dataRow: any[] = [];

      this.exportColumns.forEach((key) => {
        const col = this.columns.find((c) => c.key === key);
        if (!col) return;

        let value = this.getCellValue(row, col, rowIndex);

        if (typeof value === 'number') {
          value = Number(value.toFixed(2));
        }

        if (
          value instanceof Date ||
          moment(value, moment.ISO_8601, true).isValid()
        ) {
          value = moment(value).format('YYYY-MM-DD HH:mm');
        }

        if (Array.isArray(value)) {
          value = value.join(', ');
        }

        dataRow.push(value ?? '');
      });

      worksheet.addRow(dataRow);
    });

    worksheet.columns.forEach((col) => {
      let maxLength = 10;

      col.eachCell?.({ includeEmpty: true }, (cell) => {
        const val = cell.value ? cell.value.toString() : '';
        maxLength = Math.max(maxLength, val.length);
      });

      col.width = maxLength + 2;
    });

    const buffer = await workbook.xlsx.writeBuffer();

    this.saveExcel(buffer, 'meter-export');
  }

  async onExportRowExcel(row: any): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Meter Detail');

    const headers = this.exportColumns.map((key) => {
      const col = this.columns.find((c) => c.key === key);
      return this.translate.instant(col?.label ?? key);
    });

    worksheet.addRow(headers);

    const dataRow: any[] = [];

    this.exportColumns.forEach((key, index) => {
      const col = this.columns.find((c) => c.key === key);
      if (!col) return;

      let value = this.getCellValue(row, col, index);

      if (Array.isArray(value)) value = value.join(', ');
      if (value instanceof Date)
        value = moment(value).format('YYYY-MM-DD HH:mm');

      dataRow.push(value ?? '');
    });

    worksheet.addRow(dataRow);

    const buffer = await workbook.xlsx.writeBuffer();

    this.saveExcel(buffer, row?.name);
  }

  saveExcel(buffer: any, fileName: string): void {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    FileSaver.saveAs(blob, `${fileName}_${new Date().getTime()}.xlsx`);
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows && numRows > 0;
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.dataSource.data.forEach((row) => this.selection.select(row));
    }
  }

  get selectedCount(): number {
    return this.selection.selected.length;
  }
}
